import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, useInView } from "framer-motion";
import { FaPlay, FaCamera, FaArrowRight } from "react-icons/fa";

const MediaSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.2, once: true });

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  return (
    <SectionContainer ref={sectionRef}>
      <ContentWrapper>
        <ContentGrid>
          <TextSection>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={
                isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }
              }
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <SectionHeader>
                <SectionSubtitle>Media Gallery</SectionSubtitle>
                <SectionTitle>Take A Look</SectionTitle>
              </SectionHeader>

              <SectionDescription>
                Explore our comprehensive media collection showcasing the impact
                of our work across communities. From powerful photographs
                capturing moments of transformation to inspiring videos
                documenting our journey, discover the stories behind our
                mission.
              </SectionDescription>

              {/* <FeaturesList>
                <FeatureItem>
                  <FeatureIcon>
                    <FaCamera />
                  </FeatureIcon>
                  <FeatureText>
                    High-quality photographs from field operations
                  </FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <FaPlay />
                  </FeatureIcon>
                  <FeatureText>
                    Documentary videos and community testimonials
                  </FeatureText>
                </FeatureItem>
              </FeaturesList> */}
            </motion.div>
          </TextSection>

          <GallerySection>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            >
              <GalleryGrid>
                <GalleryCard>
                  <CardImageContainer>
                    <CardImage
                      src="https://res.cloudinary.com/dnoojbooq/image/upload/v1750318421/videoPoster_ceadiq.webp"
                      alt="Photo Gallery Preview"
                    />
                    <ImageOverlay />
                    <CardIcon>
                      <FaCamera />
                    </CardIcon>
                    <CardTitle>Photo Gallery</CardTitle>
                    <ViewButton>
                      <span>View Photo Gallery</span>
                      <FaArrowRight />
                    </ViewButton>
                  </CardImageContainer>
                </GalleryCard>

                <GalleryCard>
                  <CardImageContainer>
                    <CardImage
                      src="https://res.cloudinary.com/dnoojbooq/image/upload/v1750318421/photoPoster_dlxrx1.webp"
                      alt="Video Gallery Preview"
                    />
                    <ImageOverlay />
                    <CardIcon>
                      <FaPlay />
                    </CardIcon>
                    <CardTitle>Video Gallery</CardTitle>
                    <ViewButton>
                      <span>View Video Gallery</span>
                      <FaArrowRight />
                    </ViewButton>
                  </CardImageContainer>
                </GalleryCard>
              </GalleryGrid>
            </motion.div>
          </GallerySection>
        </ContentGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default MediaSection;

// Styled Components
const SectionContainer = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 6rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }
`;

const TextSection = styled.div`
  @media (max-width: 1024px) {
    order: 1;
  }
`;

const GallerySection = styled.div`
  @media (max-width: 1024px) {
    order: 2;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 2rem;
`;

const SectionSubtitle = styled.p`
  color: #10b981;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  margin: 0;
  letter-spacing: -0.02em;
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #64748b;
  margin: 0 0 2rem 0;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981, #059669);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
`;

const FeatureText = styled.span`
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 500;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const GalleryCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f5f9;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    border-color: #10b981;
  }
`;

const CardImageContainer = styled.div`
  position: relative;
  height: 280px;
  overflow: hidden;

  @media (max-width: 768px) {
    /* display: flex;
    flex-direction: column;
    gap: 10px; */
    height: 200px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  ${GalleryCard}:hover & {
    transform: scale(1.1);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.85) 0%,
    rgba(5, 150, 105, 0.85) 100%
  );
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);

  ${GalleryCard}:hover & {
    opacity: 1;
  }
`;

const CardIcon = styled.div`
  position: absolute;
  top: 45%;
  left: 43%;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  opacity: 1;
  transform: translateY(-10px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  ${GalleryCard}:hover & {
    opacity: 0;
    transform: translateY(0);
  }
`;

const CardTitle = styled.h3`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  text-align: center;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  ${GalleryCard}:hover & {
    opacity: 1;
    transform: translate(-50%, -50%) translateY(-10px);
  }
`;

const ViewButton = styled.button`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  color: #10b981;
  border: none;
  padding: 0.75rem 1.5rem;
  box-sizing: border-box;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  opacity: 0;
  transform: translateX(-50%) translateY(20px);

  ${GalleryCard}:hover & {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
    background: white;
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
  }

  &:hover {
    transform: translateX(-50%) translateY(-2px) !important;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2) !important;

    svg {
      transform: translateX(3px);
    }
  }

  svg {
    transition: transform 0.3s ease;
    font-size: 0.8rem;
  }
`;
